import { yupResolver } from '@hookform/resolvers/yup'
import { AuthStatus, SmsStatus } from 'feature/auth/redux/authSlice'
import { setAuthRefreshAccessToken } from 'feature/auth/redux/authStorage'
import { useRegisterCompanyMutation } from 'feature/company/redux/company.api'
import { CompanyRegistrationFormValues } from 'feature/company/types/CompanyRegistrationForm'
import { companyRegistrationSchema } from 'feature/company/validation/companyValidation'
import { MainLayout } from 'layouts'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { Loading } from 'router/Loading'
import { useActions, useAppSelector } from 'store/hooks'
import { Severity } from 'widget/Notifications'
import { NavigationPrompt } from 'widget/NavigationPrompt/NavigationPrompt'
import { CreateModalConfirm } from 'widget/CreateModalConfirm/CreateModalConfirm'

import { ContactDataStep } from './Steps/ContactDataStep'
import { CompanyDetailsStep } from './Steps/CompanyDetailsStep'
import { StepIndicator } from './components/StepIndicator'
import { companyRegistrationStorage } from './storage/companyRegistrationStorage'
import {
  buildRequestData,
  getDefaultValues,
  isStep1Complete,
} from './helpers/companyRegistrationHelpers'
import { EMAIL_REGEX } from 'constants/validation'
import styles from './CompanyRegistration.module.scss'

const STEPS: [
  { label: string; testId: string },
  { label: string; testId: string },
] = [
  { label: 'Контактные данные', testId: 'company-reg-step-1' },
  { label: 'Реквизиты компании', testId: 'company-reg-step-2' },
]

export function CompanyRegistration() {
  const navigate = useNavigate()
  const { clearMap, clearAddress, addNotification, setAuthInfo, setSmsInfo } =
    useActions()
  const { authInfo } = useAppSelector(state => state.auth)
  const [registerCompany, { isLoading }] = useRegisterCompanyMutation()

  const savedForm = companyRegistrationStorage.loadForm()
  const initialStep =
    savedForm && isStep1Complete(savedForm)
      ? companyRegistrationStorage.loadStep()
      : 1

  const [currentStep, setCurrentStep] = useState<1 | 2>(initialStep)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isExitModal, setIsExitModal] = useState(true)

  const formMethods = useForm<CompanyRegistrationFormValues>({
    defaultValues: savedForm
      ? {
          ...getDefaultValues(),
          ...savedForm,
          email: savedForm.email || getDefaultValues().email,
        }
      : getDefaultValues(),
    mode: 'onChange',
    resolver: yupResolver(companyRegistrationSchema) as any,
  })

  const [email, firstName, lastName, agree] = formMethods.watch([
    'email',
    'firstName',
    'lastName',
    'agreeProcessingPersonalData',
  ])

  const isEmailValid = EMAIL_REGEX.test(email.trim())
  const hasForbiddenComTld = (checkedEmail: string): boolean =>
    checkedEmail.trim().toLowerCase().endsWith('.com')

  const isStep1Filled = !!(
    email?.trim() &&
    isEmailValid &&
    hasForbiddenComTld(email) === false &&
    firstName?.trim() &&
    lastName?.trim() &&
    agree
  )

  useEffect(() => {
    if (
      authInfo.status !== AuthStatus.smsConfirmedCompany &&
      authInfo.status !== AuthStatus.registered
    ) {
      navigate('/company/login')
    }
  }, [authInfo.status, navigate])

  useEffect(() => {
    const subscription = formMethods.watch(data => {
      companyRegistrationStorage.saveForm(data as CompanyRegistrationFormValues)
    })
    return () => subscription.unsubscribe()
  }, [formMethods])

  const handleNextStep = () => {
    setCurrentStep(2)
    companyRegistrationStorage.saveStep(2)
  }

  const handlePreviousStep = () => {
    setCurrentStep(1)
    companyRegistrationStorage.saveStep(1)
  }

  const onConfirmExit = () => {
    clearMap()
    clearAddress()
    companyRegistrationStorage.clear()
    setAuthRefreshAccessToken(null)
    setAuthInfo({
      status: AuthStatus.guest,
      accessToken: null,
      refreshToken: null,
    })
    setSmsInfo({ status: SmsStatus.readyForSend })
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      const result = await registerCompany(
        buildRequestData(formMethods.getValues()),
      ).unwrap()

      setIsExitModal(false)
      clearMap()
      clearAddress()
      companyRegistrationStorage.clear()
      setAuthInfo({
        status: AuthStatus.registered,
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
      })
      setSmsInfo({ status: SmsStatus.readyForSend })
      if (result?.message) {
        addNotification({ text: result.message, severity: Severity.success })
      }
      navigate('/')
    } catch (err: any) {
      setIsSubmitting(false)
      addNotification({
        text: err?.data?.message || 'Ошибка регистрации. Попробуйте позже',
        severity: Severity.error,
      })
    }
  }

  if (isLoading || isSubmitting) {
    return (
      <MainLayout>
        <Loading />
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <FormProvider {...formMethods}>
        <div className='container'>
          <div className={styles.companyRegistration}>
            <StepIndicator
              currentStep={currentStep}
              isStep2Clickable={isStep1Filled}
              onStep1Click={handlePreviousStep}
              onStep2Click={handleNextStep}
              steps={STEPS}
            />
            {currentStep === 1 ? (
              <ContactDataStep onNext={handleNextStep} isLoading={isLoading} />
            ) : (
              <CompanyDetailsStep
                onPrevious={handlePreviousStep}
                onSubmit={handleSubmit}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>
      </FormProvider>
      <NavigationPrompt when={isExitModal} onConfirm={onConfirmExit}>
        {CreateModalConfirm()}
      </NavigationPrompt>
    </MainLayout>
  )
}
